import { useEffect, useState } from 'react';
import AreaRollupCard from '../components/portal/AreaRollupCard';
import Layout from '../components/Layout';
import {
  api,
  type PortalAdminRow,
  type PortalLinkRow,
  type PortalActivityRow,
  type PortalNoteAdminRow,
} from '../lib/api';
import PortalEditor from '../components/portal/editor/PortalEditor';
import { clientLinkUrl } from '../lib/hostPolicy';
import { displayDate } from '../utils/dates';

/**
 * Broker-side management for client portals (plan Task 9).
 *
 * THE ONE THING THIS SCREEN MUST GET RIGHT: the raw share token is returned by the API
 * exactly once, because only its sha256 hash is stored. If it is not copied off this page it
 * cannot be recovered - the only remedy is minting a new link. Hence the token panel sits
 * above everything else, states plainly that it will not be shown again, and survives until
 * the broker dismisses it rather than disappearing on the next re-render.
 *
 * A link's tier is a confidentiality decision, not a preference: `internal` is not offered
 * here and is rejected server-side, so nothing a broker can do from this page exposes the
 * strategy content.
 */

const TIERS = [
  { value: 'family', label: 'Family — everything except internal strategy' },
  { value: 'buyer', label: 'Buyer — no net proceeds, no per-parcel tax status' },
  { value: 'public', label: 'Public — headline overview only' },
];

const fmtDate = (v?: string | null) =>
  displayDate(v, { year: 'numeric', month: 'short', day: 'numeric' }) ?? '—';

const fmtDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

/** Revoked beats expired beats active - a revoked link that also expired is still revoked. */
const linkState = (l: PortalLinkRow): 'Revoked' | 'Expired' | 'Active' => {
  if (l.revoked_at) return 'Revoked';
  if (l.expires_at && new Date(l.expires_at).getTime() < Date.now()) return 'Expired';
  return 'Active';
};

const STATE_STYLE: Record<string, string> = {
  Active: 'bg-success-wash text-success border-success/30',
  Expired: 'bg-warning-wash text-warning border-warning/30',
  Revoked: 'bg-gray-100 text-gray-500 border-gray-300',
};

export default function PortalsAdmin() {
  const [portals, setPortals] = useState<PortalAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [activity, setActivity] = useState<Record<string, PortalActivityRow[]>>({});
  const [activityError, setActivityError] = useState('');
  const [notes, setNotes] = useState<Record<string, PortalNoteAdminRow[]>>({});
  const [notesError, setNotesError] = useState('');

  // Mint form
  const [tier, setTier] = useState('family');
  const [label, setLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [minting, setMinting] = useState(false);
  const [minted, setMinted] = useState<{ portalId: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listPortals();
      setPortals(res.portals ?? []);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load portals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadNotes = async (id: string) => {
    try {
      const res = await api.listPortalNotesAdmin(id);
      setNotes((m) => ({ ...m, [id]: res.notes ?? [] }));
    } catch (e) {
      setNotesError(e instanceof Error ? e.message : 'Could not load notes');
    }
  };

  const toggle = async (id: string) => {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    setActivityError('');
    setNotesError('');
    // Two independent reads, deliberately not chained: a failure to load activity must not
    // also blank the notes panel (and the other way round), so each keeps its own error.
    await Promise.all([
      api
        .getPortalActivity(id)
        .then((res) => setActivity((a) => ({ ...a, [id]: res.activity ?? [] })))
        .catch((e) =>
          setActivityError(e instanceof Error ? e.message : 'Could not load activity')
        ),
      loadNotes(id),
    ]);
  };

  /**
   * Moderation refetches rather than patching the row in place: `hidden_at` is a server
   * timestamp, so the only way to show the true value is to read it back.
   */
  const toggleNoteHidden = async (portalId: string, note: PortalNoteAdminRow) => {
    setNotesError('');
    try {
      await api.setPortalNoteHidden(portalId, note.id, !note.hidden_at);
      await loadNotes(portalId);
    } catch (e) {
      setNotesError(e instanceof Error ? e.message : 'Could not update note');
    }
  };

  const mint = async (portalId: string) => {
    if (!label.trim()) {
      setError('Give the link a label so it can be told apart later.');
      return;
    }
    setMinting(true);
    setError('');
    try {
      const res = await api.mintPortalLink(portalId, {
        tier,
        label: label.trim(),
        expiresAt: expiresAt || null,
      });
      setMinted({ portalId, token: res.token });
      setCopied(false);
      setLabel('');
      setExpiresAt('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not mint link');
    } finally {
      setMinting(false);
    }
  };

  const revoke = async (portalId: string, linkId: string) => {
    setError('');
    try {
      await api.revokePortalLink(portalId, linkId);
      await load();
    } catch (e) {
      // Revocation is admin-only server-side; a 403 comes back with its own message and is
      // shown as-is rather than being flattened into "something went wrong".
      setError(e instanceof Error ? e.message : 'Could not revoke link');
    }
  };

  // ⛔ The CLIENT origin, never this page's own. Staff mint from `app.apex-res.com`; the client
  // must receive `portal.apex-res.com`. ▶ `lib/hostPolicy.ts`.
  const mintedUrl = minted ? clientLinkUrl(`/portal/${minted.token}`) : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(mintedUrl);
      setCopied(true);
    } catch {
      // Clipboard access can be refused (insecure context, permissions). The URL is already
      // on screen in a selectable field, so this degrades to "select it yourself".
      setCopied(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-[1600px] px-4 py-6">
        <div className="mb-6">
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Client Portals</h1>
          <p className="text-sm text-gray-500 mt-1">
            Tokenized share links to a client-facing deal portal. A link's tier decides what the
            payload contains — filtering happens server-side, so higher-tier content never
            reaches the browser.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {minted && (
          <div className="mb-6 p-4 rounded-lg border-2 border-primary-500 bg-red-50">
            <p className="text-sm font-bold text-primary-500 mb-1">
              Copy this link now — it will not be shown again.
            </p>
            <p className="text-xs text-gray-600 mb-3">
              Only a hash of the token is stored. If this is lost, mint a new link and revoke
              this one.
            </p>
            <div className="flex gap-2 flex-wrap">
              <input
                readOnly
                value={mintedUrl}
                aria-label="Portal share link"
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 min-w-[260px] px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs bg-white"
              />
              <button
                onClick={copy}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-red-700"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button
                onClick={() => setMinted(null)}
                className="px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-gray-500">Loading portals…</p>
        ) : !portals.length ? (
          <div className="p-8 text-center border border-gray-200 rounded-xl bg-white">
            <p className="text-gray-500">
              No portals yet. Import one with{' '}
              <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">
                backend/scripts/import-portal.ts
              </code>
              .
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* AREA1: one "who opened" card per area, above the pages it rolls up. */}
            {[...new Set(portals.map((p) => p.area).filter((a): a is string => !!a))].sort().map((area) => (
              <AreaRollupCard key={area} area={area} />
            ))}
            {portals.map((p) => {
              const links = p.links ?? [];
              const live = links.filter((l) => linkState(l) === 'Active').length;
              const isOpen = expanded === p.id;
              return (
                <div key={p.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggle(p.id)}
                    className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{p.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        /{p.slug} · {links.length} link{links.length === 1 ? '' : 's'} · {live} active ·
                        updated {fmtDate(p.updated_at)}
                      </p>
                    </div>
                    <span className="text-gray-400 ml-3 flex-shrink-0">{isOpen ? '▲' : '▼'}</span>
                  </button>

                  {isOpen && (
                    <div className="border-t border-gray-200 px-5 py-4 space-y-6">
                      {/* Update the portal (VP9): status, timeline, open items - the everyday updates, with history and undo. */}
                      <div className="pb-6 border-b border-gray-200">
                        <PortalEditor portalId={p.id} />
                      </div>

                      {/* ── Mint ── */}
                      <div>
                        <h2 className="text-sm font-semibold text-gray-900 mb-2">Mint a link</h2>
                        <div className="flex flex-wrap gap-2 items-end">
                          <label className="flex flex-col text-xs text-gray-500">
                            Tier
                            <select
                              value={tier}
                              onChange={(e) => setTier(e.target.value)}
                              className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 min-w-[220px]"
                            >
                              {TIERS.map((t) => (
                                <option key={t.value} value={t.value}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="flex flex-col text-xs text-gray-500 flex-1 min-w-[200px]">
                            Label
                            <input
                              value={label}
                              onChange={(e) => setLabel(e.target.value)}
                              placeholder="Who is this link for?"
                              className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900"
                            />
                          </label>
                          <label className="flex flex-col text-xs text-gray-500">
                            Expires (optional)
                            <input
                              type="date"
                              value={expiresAt}
                              onChange={(e) => setExpiresAt(e.target.value)}
                              className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900"
                            />
                          </label>
                          <button
                            onClick={() => mint(p.id)}
                            disabled={minting}
                            className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
                          >
                            {minting ? 'Minting…' : 'Mint link'}
                          </button>
                        </div>
                      </div>

                      {/* ── Links ── */}
                      <div>
                        <h2 className="text-sm font-semibold text-gray-900 mb-2">Links</h2>
                        {!links.length ? (
                          <p className="text-sm text-gray-500">No links minted yet.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                                  <th className="py-2 pr-3">Label</th>
                                  <th className="py-2 pr-3">Tier</th>
                                  <th className="py-2 pr-3">Created</th>
                                  <th className="py-2 pr-3">Expires</th>
                                  <th className="py-2 pr-3">Status</th>
                                  <th className="py-2" />
                                </tr>
                              </thead>
                              <tbody>
                                {links.map((l) => {
                                  const state = linkState(l);
                                  return (
                                    <tr key={l.id} className="border-b border-gray-100">
                                      <td className="py-2 pr-3 text-gray-900">{l.label}</td>
                                      <td className="py-2 pr-3 capitalize">{l.tier}</td>
                                      <td className="py-2 pr-3 text-gray-500">{fmtDate(l.created_at)}</td>
                                      <td className="py-2 pr-3 text-gray-500">{fmtDate(l.expires_at)}</td>
                                      <td className="py-2 pr-3">
                                        <span
                                          className={`px-2 py-0.5 rounded-full border text-xs ${STATE_STYLE[state]}`}
                                        >
                                          {state}
                                        </span>
                                      </td>
                                      <td className="py-2 text-right">
                                        {state !== 'Revoked' && (
                                          <button
                                            onClick={() => revoke(p.id, l.id)}
                                            className="text-xs text-red-600 hover:underline"
                                          >
                                            Revoke
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* ── Family notes ── */}
                      <div>
                        <h2 className="text-sm font-semibold text-gray-900 mb-2">
                          Family notes{' '}
                          <span className="font-normal text-gray-500">
                            ({(notes[p.id] ?? []).length})
                          </span>
                        </h2>
                        {notesError && <p className="text-sm text-red-600">{notesError}</p>}
                        {!notes[p.id]?.length ? (
                          <p className="text-sm text-gray-500">No notes yet.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                                  <th className="py-2 pr-3">Anchor</th>
                                  <th className="py-2 pr-3">Author</th>
                                  <th className="py-2 pr-3">Note</th>
                                  <th className="py-2 pr-3">Via link</th>
                                  <th className="py-2 pr-3">When</th>
                                  <th className="py-2" />
                                </tr>
                              </thead>
                              <tbody>
                                {notes[p.id].map((n) => (
                                  <tr
                                    key={n.id}
                                    className={`border-b border-gray-100 ${n.hidden_at ? 'opacity-50' : ''}`}
                                  >
                                    <td className="py-2 pr-3 font-mono text-xs">
                                      {n.anchor_type}:{n.anchor_key}
                                    </td>
                                    <td className="py-2 pr-3 text-gray-900">{n.author_name}</td>
                                    <td className="py-2 pr-3 text-gray-700 max-w-[360px] whitespace-pre-line">
                                      {n.body}
                                    </td>
                                    {/* A null label is ambiguous - deleted link, or the links
                                        read failed and every label came back empty. Both the
                                        text and the tooltip key off `== null` so they cannot
                                        disagree: a truthiness test here would render an
                                        empty-string label blank AND tag it with the tooltip,
                                        claiming a missing link that is in fact present. */}
                                    <td
                                      className="py-2 pr-3 text-gray-500"
                                      title={
                                        n.link_label == null
                                          ? 'link deleted or lookup unavailable'
                                          : undefined
                                      }
                                    >
                                      {n.link_label ?? '—'}
                                    </td>
                                    <td className="py-2 pr-3 text-gray-500">
                                      {fmtDateTime(n.created_at)}
                                    </td>
                                    <td className="py-2 text-right">
                                      <button
                                        onClick={() => toggleNoteHidden(p.id, n)}
                                        className="text-xs text-red-600 hover:underline"
                                      >
                                        {n.hidden_at ? 'Unhide' : 'Hide'}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* ── Activity ── */}
                      <div>
                        <h2 className="text-sm font-semibold text-gray-900 mb-2">
                          Activity <span className="font-normal text-gray-500">(last 50)</span>
                        </h2>
                        {activityError && <p className="text-sm text-red-600">{activityError}</p>}
                        {!activity[p.id]?.length ? (
                          <p className="text-sm text-gray-500">No views or downloads logged yet.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                                  <th className="py-2 pr-3">Event</th>
                                  <th className="py-2 pr-3">When</th>
                                  <th className="py-2 pr-3">Document</th>
                                  <th className="py-2">Client</th>
                                </tr>
                              </thead>
                              <tbody>
                                {activity[p.id].map((a) => (
                                  <tr key={a.id} className="border-b border-gray-100">
                                    <td className="py-2 pr-3 capitalize text-gray-900">{a.event}</td>
                                    <td className="py-2 pr-3 text-gray-500">{fmtDateTime(a.at)}</td>
                                    <td className="py-2 pr-3 text-gray-500 font-mono text-xs">
                                      {a.doc_id ? a.doc_id.slice(0, 8) : '—'}
                                    </td>
                                    <td
                                      className="py-2 text-gray-500 text-xs max-w-[280px] truncate"
                                      title={a.user_agent ?? ''}
                                    >
                                      {a.user_agent ? a.user_agent.slice(0, 60) : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
